// json-schema-text-from-zod.ts
//
// The PURE half of the schema generator: zod schema -> deterministic JSON
// Schema text (sorted keys recursively, 2-space indent, trailing newline).
// Split out of generate-json-schemas-from-zod.ts (plan 20260915-1650
// phase-01, F2) because that file is also a CLI ENTRYPOINT guarded by
// `import.meta.url === file://${process.argv[1]}` — inside the tsup bundle
// both sides are `dist/index.js`, so importing it into a CLI command ran the
// generator on every `avatar` invocation (measured: 22 e2e failures, stdout
// polluted with "wrote …", 11 files written outside the workspace). Anything
// the CLI itself needs lives here; the entrypoint keeps the file-writing
// half and re-exports this function for the parity test.
import { type ZodType, z } from "zod";
import { omitControllerOwnedResultFields } from "./result-json-schema-controller-owned-fields.js";
import { AUTOPILOT_SCHEMAS } from "./schemas/index.js";

type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

function sortKeysDeep(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value !== null && typeof value === "object") {
    const out: { [key: string]: JsonValue } = {};
    for (const key of Object.keys(value).sort()) {
      out[key] = sortKeysDeep((value as { [key: string]: JsonValue })[key] as JsonValue);
    }
    return out;
  }
  return value;
}

/** Deterministic JSON Schema text for one registered autopilot schema name. */
export function generateJsonSchemaText(schemaName: string): string {
  const zodSchema: ZodType | undefined = AUTOPILOT_SCHEMAS[schemaName];
  if (zodSchema === undefined) {
    throw new Error(`generate-json-schemas: chưa khai zod schema cho "${schemaName}"`);
  }
  // D7 (R-18, phase 06): `config` alone uses io:"input" — z.toJSONSchema's
  // default ("output") lists every key with a `.default()` as `required`,
  // which is backwards for a file a human hand-writes (defaults exist
  // precisely so those keys may be OMITTED). Every other schema keeps the
  // default "output" mode (unchanged parity for binding/mission/etc).
  const raw = z.toJSONSchema(
    zodSchema,
    schemaName === "config" ? { io: "input" } : undefined,
  ) as JsonValue;
  // `result` is the WORKER contract: controller-computed fields are hidden here
  // as in the argv string (result-json-schema-controller-owned-fields.ts).
  const published = schemaName === "result" ? omitControllerOwnedResultFields(raw) : raw;
  const sorted = sortKeysDeep(published);
  return `${JSON.stringify(sorted, null, 2)}\n`;
}
